import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const ICONS = { team_workspace: "Network", leave: "Plane", clocking: "Clock" };

// Super-Admin control for Team Workspace areas. Unlike the modules above, these are OFF unless switched on.
export function WorkspaceModulesEditor({ orgId }) {
  const { orgs, setOrgs } = useOrg();
  const [mods, setMods] = useState(null);
  const [saving, setSaving] = useState(null);
  useEffect(() => {
    api.get(`/organisations/${orgId}/workspace-modules`).then((r) => setMods(r.data.modules)).catch(() => setMods([]));
  }, [orgId]);

  const toggle = async (m, enabled) => {
    setSaving(m.key);
    try {
      const { data } = await api.put(`/organisations/${orgId}/workspace-modules`, { module_access: { [m.key]: enabled } });
      setMods(data.modules);
      const access = Object.fromEntries(data.modules.map((x) => [x.key, x.enabled]));
      setOrgs(orgs.map((o) => (o.id === orgId ? { ...o, module_access: { ...(o.module_access || {}), ...access } } : o)));
      toast.success(`${m.label} ${enabled ? "switched on" : "switched off"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  if (!mods) return null;
  return (
    <div className="mt-6" data-testid="workspace-modules-editor">
      <div className="label-eyebrow mb-1.5">Team Workspace</div>
      <p className="mb-3 text-xs text-[#1E2D44]/55">Lets the company's employees sign in to see their linked work, the KPIs they report and company resources. Off unless you switch it on.</p>
      <div className="space-y-1.5">
        {mods.map((m) => (
          <div key={m.key} className="flex items-center gap-3 rounded-lg border border-[#E4E8EF] px-3.5 py-2.5" data-testid={`ws-module-row-${m.key}`}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.enabled ? "bg-[#0B1729] text-[#FFC24D]" : "bg-[#F4F6F9] text-[#1E2D44]/35"}`}>
              {React.createElement(Icons[ICONS[m.key]] || Icons.Circle, { size: 16 })}
            </span>
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-medium ${m.enabled ? "text-[#0B1729]" : "text-[#1E2D44]/55"}`}>{m.label}</div>
              <div className="text-[11px] text-[#1E2D44]/45">{!m.available ? "Arrives in a later release" : m.enabled ? "Switched on for this company" : "Off"}</div>
            </div>
            {saving === m.key
              ? <Icons.Loader2 className="animate-spin text-[#FFB000]" size={16} />
              : <Switch checked={m.enabled} disabled={!m.available} onCheckedChange={(v) => toggle(m, v)} data-testid={`ws-module-toggle-${m.key}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}
